/**
 * Credits Service for Web App
 * Manages AI credit balance and purchases via Firebase Functions
 */

import { functions } from './firebase-config.js';
import { httpsCallable } from 'firebase/functions';

// Credit packages available for purchase
export const CREDIT_PACKAGES = [
    { id: 'credits-50', name: '50 Credits', credits: 50, price: '$2.99' },
    { id: 'credits-200', name: '200 Credits', credits: 200, price: '$9.99' },
    { id: 'credits-500', name: '500 Credits', credits: 500, price: '$19.99' }
];

// Cache for credit balance
let cachedCredits = null;
let cacheTimestamp = 0;
const CACHE_TTL = 30000; // 30 seconds

/**
 * Get current credit balance from Firebase
 * @param {boolean} forceRefresh - Skip cache and fetch fresh data
 * @returns {Promise<Object>} Credit balance object
 */
export async function getCreditsBalance(forceRefresh = false) {
    // Return cached if fresh
    if (!forceRefresh && cachedCredits && (Date.now() - cacheTimestamp) < CACHE_TTL) {
        return cachedCredits;
    }

    try {
        const callable = httpsCallable(functions, 'getCreditsBalance');
        const result = await callable({});

        cachedCredits = result.data;
        cacheTimestamp = Date.now();

        return cachedCredits;
    } catch (error) {
        console.error('Error fetching credit balance:', error);

        // Return cached data if available, otherwise throw
        if (cachedCredits) {
            return cachedCredits;
        }

        throw error;
    }
}

/**
 * Clear the credit cache (call after AI operations)
 */
export function clearCreditCache() {
    cachedCredits = null;
    cacheTimestamp = 0;
}

/**
 * Update cached credits after an AI operation
 * @param {number} remaining - Remaining credits returned from AI function
 */
export function updateCachedCredits(remaining) {
    if (cachedCredits) {
        cachedCredits.totalRemaining = remaining;
    }
}

/**
 * Add purchased credits (called after purchase confirmation)
 * @param {number} amount - Number of credits to add
 * @param {string} purchaseId - Purchase ID
 * @returns {Promise<Object>} Result with success status
 */
export async function addPurchasedCredits(amount, purchaseId) {
    try {
        const callable = httpsCallable(functions, 'addPurchasedCredits');
        const result = await callable({ amount, purchaseId });

        // Clear cache to get fresh balance
        clearCreditCache();

        return result.data;
    } catch (error) {
        console.error('Error adding purchased credits:', error);
        throw error;
    }
}

/**
 * Check if user has credits available
 * @returns {Promise<boolean>}
 */
export async function hasCreditsAvailable() {
    try {
        const balance = await getCreditsBalance();
        return balance.isPro && balance.totalRemaining > 0;
    } catch {
        return false;
    }
}

/**
 * Format credits display string
 * @param {Object} balance - Credit balance object
 * @returns {string} Formatted display string
 */
export function formatCreditsDisplay(balance) {
    if (!balance || !balance.isPro) {
        return 'Pro required';
    }

    const { monthlyCredits, purchasedCredits, monthlyLimit } = balance;
    const total = monthlyCredits + purchasedCredits;

    if (purchasedCredits > 0) {
        return `${total} credits (${monthlyCredits}/${monthlyLimit} monthly + ${purchasedCredits} bonus)`;
    }

    return `${monthlyCredits}/${monthlyLimit} credits`;
}

/**
 * Get a short credits summary for display
 * @param {Object} balance - Credit balance object
 * @returns {string} Short summary
 */
export function getCreditsShortDisplay(balance) {
    if (!balance || !balance.isPro) {
        return '—';
    }

    const total = balance.monthlyCredits + balance.purchasedCredits;
    return `${total}`;
}

